import { Fragment, memo, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  ActivityBlock,
  DerivedReferenceBlock,
  ErrorBlock,
  ProseBlock,
  ReasoningBlock,
  ReferenceBlock,
  SourcesRow,
} from "./TranscriptBlocks";
import {
  cardedTargets,
  groupTurnParts,
  outcomeClosingLine,
  partText,
  proseOf,
  preserveLineBreaksOutsideFences,
  sourcesOf,
  type ChangePreview,
  type Turn,
} from "@/services/multi-agent/transcript";
import { RunErrorRailView } from "@/components/organisms/multi-agent/error/RunErrorRail";
import { ModeChip } from "@/components/molecules/multi-agent/ModeChip";
import { CheckCircle2 } from "lucide-react";
import type { Evidence, Part } from "@/services/multi-agent/protocol";
import type { WorkspaceSection } from "@/stores/weaveWorkspaceStore";
import type { OpenableSection, ReferenceTarget } from "@/services/multi-agent/transcript";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { TurnAttachmentChips } from "@/components/organisms/multi-agent/laneV2/TurnAttachmentChips";
import type { ConversationAttachment } from "@/services/multi-agent/laneV2/reduce";
import { SourcesReadBlock } from "@/components/molecules/multi-agent/SourcesReadBlock";
import { splitSourcesTail, sourcesReadCount } from "@/services/multi-agent/laneV2/sourcesTail";

/**
 * One user → assistant turn.
 *
 * `memo`'d on the turn's message identities, which is what makes a 30/s `part_delta` affordable:
 * the reducer's `mapMessages` keeps untouched messages referentially stable, so a delta produces a
 * new object for the streaming message only and every past turn's props are unchanged.
 */
export interface TurnViewProps {
  turn: Turn;
  /** Live subtitles for reference cards, keyed by target — "3 steps · config". */
  subtitles?: Partial<Record<ReferenceTarget, string | null>>;
  /**
   * The LIVE run's produced code, for a `changeset` card this turn carries as a part
   * (`changePreviews(ownChangeset)`). Only the live turn gets this; a past turn's previews travel
   * with its own `references`.
   */
  changesetPreviews?: { previews: ChangePreview[]; hidden: number } | null;
  onOpenWorkspace: (section: OpenableSection, focusRef: string | null) => void;
  activeSection?: WorkspaceSection | null;
  /**
   * Which `Part.ref` the open section is showing, when the section is about one specific thing.
   *
   * Needed because `activeSection` alone is not enough to say whether THIS card is the open one:
   * a turn can reference two documents, and comparing only the section made both `artifact` cards
   * read "Close" as soon as either was open. The other four sections have one subject per run, so
   * their cards are unambiguous either way.
   */
  activeRef?: string | null;
  /**
   * The `pre` override that makes fenced blocks openable — from `buildCodeInterceptor`.
   *
   * Built once by the transcript and passed down rather than created per turn: a fresh object each
   * render would break `ProseBlock`'s memo and re-parse every past turn's markdown on every frame.
   */
  codeInterceptor?: { codeRenderers?: readonly unknown[]; components?: Record<string, unknown> };
  /**
   * Reference part types the transcript renders somewhere better — see `groupParts`. Must be a
   * STABLE set: a fresh `Set` per render defeats `memo` on every past turn.
   */
  suppressReferences?: ReadonlySet<Part["type"]>;
  /**
   * Something that is NOT a part — the plan block, the outputs — rendered at the point in the turn
   * where it ARRIVED rather than after everything.
   *
   * `afterParts` is how many of this turn's parts existed when it first appeared; the node renders
   * after the block that carries that many parts, or last when the count is never reached
   * (`Infinity` = "we do not know", the reload case). This is what lets the response keep growing
   * UNDER the plan.md card instead of the card sitting at the bottom while new thinking piles up
   * above it.
   */
  interlude?: { afterParts: number; node: ReactNode } | null;
  /**
   * Is this turn's run in `turnPhase`'s `"working"` state — parts exist, no visible prose yet, run
   * not complete? Only ever `true` for the LAST turn; every earlier one is settled by definition, so
   * `WeaveTranscript` passes `false` for those without needing a second phase computation per turn.
   *
   * WHY THIS EXISTS: see the reasoning block below.
   */
  turnWorking?: boolean;
  /**
   * The run's evidence checks (`RunState.evidence`), for the evidence accordion of the LIVE turn.
   * They ride in the turn's last tool-call group — "what the run looked at, and what it verified" is
   * one panel — or in a group of their own when the turn made no tool calls.
   */
  checks?: readonly Evidence[];
  /**
   * What the reader attached to THIS question (lane v2), shown under their bubble. Rendered here rather than by the
   * caller because the caller only sees the whole turn: placed after `TurnView`, the chips landed below the answer.
   */
  userAttachments?: readonly ConversationAttachment[];
  /**
   * Lane V2 only (leg `gui-what-i-read`): the answer's code-written `## Evidence` tail is shown as a collapsed "Sources read (N)"
   * block under the prose; `sourcesToolCalls` is R5 `trace.tool_calls`. Omitted, the markdown renders as before.
   */
  sourcesTail?: boolean;
  /** R5 `trace.tool_calls` for the "Sources read (N)" count. A primitive, so the shallow `memo` keeps past turns still. */
  sourcesToolCalls?: number | null;
  /** Lane V2: the task's outcome card, placed ABOVE the "Sources read" block (or after the prose when the answer has none). */
  outcome?: ReactNode;
}

/**
 * How many lines of the reader's own question stay on screen before it collapses.
 *
 * A pasted stack trace or a 600-word spec arrives as ONE bubble, and at full height it pushed the
 * answer it was asking about entirely below the fold — the reader had to scroll past their own
 * words to reach the reply. Twelve lines is enough to recognise what was asked without the question
 * outweighing the answer.
 *
 * Measured in LINES rather than characters: what matters is the vertical space it takes, and a
 * character count gets that wrong the moment the text is a narrow list or a wide paragraph.
 */
// Four, not twelve, since the question is PINNED at the top while its answer scrolls (owner 2026-11-02): a pinned block
// that tall would cover the answer it heads. "Show more" still opens the whole question.
const USER_TURN_CLAMP_LINES = 4;

/**
 * The reader's own words, collapsible when they are long.
 *
 * A height cap (see the style below) rather than slicing the string: the full text stays in the DOM, so it is
 * selectable, searchable with the browser's own find, and read in full by a screen reader whether
 * or not it is expanded. Truncating the value would hide it from all three.
 *
 * `scrollHeight > clientHeight` decides whether the toggle appears AT ALL — a question that fits
 * gets no control, so the common case is exactly what it was before.
 */
/** Exported for direct testing — see `TurnView.test.tsx` — same reason `ClosingLine` below is. */
export function UserBubble({
  text,
  codeInterceptor,
}: {
  text: string;
  /** From `buildCodeInterceptor` — the same one `ProseBlock` renders the assistant's own text
   *  with, threaded through so a reader's pasted code gets the identical fence treatment
   *  (long-block preview, copy/download) as a class the agent itself prints. Omitted, a fenced
   *  block still renders as ordinary markdown code — see `SkillResultMarkdown`'s own contract. */
  codeInterceptor?: { codeRenderers?: readonly unknown[]; components?: Record<string, unknown> };
}) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measured in a layout effect so the first paint already knows — a toggle that appears one
    // frame late reads as the bubble twitching.
    setClamped(el.scrollHeight - el.clientHeight > 2);
  }, [text]);

  return (
    <div className="flex w-full flex-col items-start gap-1">
      {/*
        `data-weave-user-turn` IS A TEST SEAM, and a deliberate one. The route-level QA driver has
        to answer "is the reader seeing the right question, once" — the defect class where a
        follow-up rewrote or duplicated the previous one — and the only alternative is selecting on
        Tailwind classes, which makes a styling change look like a behaviour regression. Same idea
        as `data-testid="chat-code-body"` a few files over.
      */}
      <div
        ref={ref}
        data-weave-user-turn=""
        /*
          ── THE READER'S TURN: A SOFT FILL AND A HAIRLINE, NOTHING LOUDER ──────────────────────
          Two earlier versions bracket this one. `bg-muted/60` with no border was reported as
          indistinguishable from the answer on the dark ground. The fix over-corrected: a white card,
          a grey rule, a drop shadow and a 3px accent edge — the only boxed, shadowed, blue-edged
          thing in a column of flat prose, which on the light ground read as a form control, and
          once the route's blue came to mean "selected" the edge read as a selection.
          `--ma-user-bubble` is a fill defined PER THEME a clear step off each ground, plus a hairline
          so the shape holds at low contrast, full width at the left edge. The text stays ordinary prose:
          these are the reader's own words, not an alert.

          ── A `<div>`, NOT A `<p>`, AND MARKDOWN, NOT LITERAL TEXT ─────────────────────────────
          REPORTED: pasting a short multi-line snippet from an editor and sending it produced an
          unreadable "User Question" — no monospace, no background, no preserved indentation. This
          rendered raw `{text}` inside a `<p>`, which is why: a `<p>` cannot legally contain the
          block content (`<pre>`, lists, headings) markdown produces, and nothing here parsed
          markdown in the first place. `SkillResultMarkdown` is the SAME renderer every assistant
          answer already uses — reused, not reinvented — so a genuinely fenced code block (one the
          composer wrapped on paste, or one the reader typed themselves) gets the identical
          monospace/background/scroll treatment, in both themes, that this codebase already
          verified for the answer side. A message with no fence in it renders as ordinary prose,
          which is what plain text already looked like — `.ma-answer-quiet` is the same tightened
          density `ReasoningBlock` uses for a compact block, not full answer spacing.
        */
        className="w-full break-words rounded-lg border border-border/70 bg-[var(--ma-user-bubble)] px-4 py-2.5 text-[14.5px] leading-relaxed text-foreground"
        style={{
          // Clamp and surface in one object: two `style` props are a syntax error, and splitting
          // them across `className` would put a magic line count in a utility string.
          // A HEIGHT cap, not `-webkit-line-clamp`: the question renders as markdown paragraphs, and line-clamp only counts the
          // lines of a box's own inline text, so it clamped nothing (dev 2026-11-03: a 7-line question showed in full, 190px,
          // with no "Show more"). Lines × the box's leading (`leading-relaxed` = 1.625) + its vertical padding (`py-2.5`).
          ...(expanded
            ? null
            : {
                maxHeight: `calc(${USER_TURN_CLAMP_LINES} * 1.625em + 1.25rem)`,
                overflow: "hidden",
              }),
        }}
      >
        <SkillResultMarkdown
          content={preserveLineBreaksOutsideFences(text)}
          className="ma-answer ma-answer-quiet"
          codeRenderers={codeInterceptor?.codeRenderers}
          components={codeInterceptor?.components}
        />
      </div>
      {clamped && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="inline-flex min-h-6 items-center rounded-md px-1.5 py-0.5 text-[12px] font-medium text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-muted/60 hover:text-foreground"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

export const TurnView = memo(function TurnView({
  turn,
  subtitles,
  changesetPreviews = null,
  onOpenWorkspace,
  activeSection,
  activeRef,
  codeInterceptor,
  suppressReferences,
  turnWorking = false,
  interlude = null,
  checks,
  userAttachments,
  sourcesTail = false,
  sourcesToolCalls = null,
  outcome = null,
}: TurnViewProps) {
  const userText = turn.user ? turn.user.parts.map(partText).join("").trim() : "";
  /*
   * ── ONE CARD PER SUBJECT, whichever way it was produced ────────────────────────────────────────
   *
   * A past run's changeset can reach this turn twice: as a `changeset` PART the run emitted (a
   * `ReferenceBlock`) and as the archive's derived card (`turn.references`, stamped for every
   * archived run that built one). Both said "Changes"; two openers for one board. The part is the
   * producer's own card, so it wins — and it inherits the archived card's previews, which is where
   * the past run's code lives once it is no longer the live one.
   */
  const carded = cardedTargets(turn.assistant);
  const archivedChanges = turn.references?.find((r) => r.target === "changeset") ?? null;
  const references = (turn.references ?? []).filter((r) => !carded.has(r.target));
  const partPreviews =
    changesetPreviews ??
    (archivedChanges?.previews?.length
      ? { previews: archivedChanges.previews, hidden: archivedChanges.previewsHidden ?? 0 }
      : null);

  return (
    // `gap-2.5` between the user bubble and the answer, and the same between blocks. The first
    // version used `gap-3` here with `gap-6` between turns, and with `SkillResultMarkdown`'s own
    // paragraph margins on top the result was bands of empty space between every block — the
    // screenshot showed roughly 90px between "Thinking" and the prose it belonged to.
    <article className="flex flex-col gap-2.5">
      {/*
        The user's own words in a full-width box pinned at the top of their turn — the one place in this surface where a
        box is right, because it marks whose turn it is. Everything the assistant says is
        full-width prose, which is what makes it read as a document rather than a chat log.
      */}
      {/*
        THE APPROVAL, NOT THE QUESTION AGAIN. The run that executes an approved plan is created with
        the original request as its intent, so the conversation used to show the same question
        twice, word for word. What the reader did was approve — so that is what this turn says.
      */}
      {/*
        AN EVENT ROW, NOT A BUBBLE. The approval used to render as a right-aligned card in the
        reader's own column — a message the reader never typed, wearing the chrome of one, with the
        mode chip hanging under it as if a second message had been sent. What happened is that the
        plan crossed from proposal to execution, which is a moment in the conversation rather than
        a speaker in it: a ruled line, the fact, the mode it runs in, the goal in quiet ink.
      */}
      {turn.approval && (
        <div
          data-weave-approval-turn=""
          role="status"
          className="flex flex-col gap-1 border-t border-border/70 pt-3"
        >
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-foreground">
            <CheckCircle2 aria-hidden className="h-4 w-4 shrink-0 text-[var(--ma-state-ok-text)]" />
            <span className="font-medium">Plan v{turn.approval.planVersion} approved</span>
            {turn.mode && turn.mode !== "read" && (
              <>
                <span aria-hidden className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">Executing in</span>
                <ModeChip mode={turn.mode} />
              </>
            )}
          </p>
          <p className="break-words pl-6 text-[13px] leading-relaxed text-muted-foreground">{turn.approval.goal}</p>
        </div>
      )}
      {userText && !turn.approval && (
        /*
          PINNED WHILE ITS ANSWER SCROLLS (owner 2026-11-02, "the user question at top", as an editor's chat panel does).
          `sticky` inside this turn's own <article>, so it stays at the top only while this turn's answer is on screen and
          the next turn's question pushes it away. The opaque ground hides the answer passing underneath it; full width and
          left-aligned so question, answer and composer share one edge.
        */
        <div data-weave-pinned-question="" className="sticky top-0 z-[5] -mt-2 bg-background pb-1 pt-2">
          {/*
            `data-weave-user-turn` IS A TEST SEAM, and a deliberate one. The route-level QA driver
            has to answer "is the reader seeing the right question, once" — the defect class where a
            follow-up rewrote or duplicated the previous one — and the only alternative is selecting
            on Tailwind classes, which makes a styling change look like a behaviour regression. Same
            idea as `data-testid="chat-code-body"` a few files over.
          */}
          <div className="flex w-full flex-col items-start gap-1">
            <UserBubble text={userText} codeInterceptor={codeInterceptor} />
            {userAttachments && userAttachments.length > 0 && <TurnAttachmentChips attachments={userAttachments} />}
            {/*
              THE MODE THE READER SENT THIS IN, under their own words — where an editor's plan mode
              shows its badge. Only for the two modes a reader chooses on purpose: Read is the
              unmarked default, and it is also what a run created without a hint reports, so a Read
              chip would claim a decision nobody made. `run.mode` is the mode ASKED for, echoed by
              the backend; the chip never says what the run did.
            */}
            {turn.mode && turn.mode !== "read" && <ModeChip mode={turn.mode} />}
          </div>
        </div>
      )}

      {/*
        ONE FLOW FOR THE WHOLE TURN, not one per message.
        
        This mapped `turn.assistant` and grouped each message on its own, so the collapse that turns
        a run of same-kind parts into a single line stopped dead at every message boundary: two
        "Thinking" disclosures stacked together, two "Used N tools" lines, and a citation repeated
        in as many rows as messages that cited it. How many messages the backend split an answer
        across is not something the reader should be able to count.
      */}
      {(() => {
        const blocks = groupTurnParts(turn.assistant, suppressReferences);
        /*
         * Where the interlude lands: after the first block whose cumulative part count reaches
         * `afterParts`. Counted over the parts the blocks carry; a block with a single part counts
         * one. `-0.5` = before the first block (it arrived before any part), `-1` = after the last.
         */
        let interludeAt = -1;
        if (interlude && Number.isFinite(interlude.afterParts)) {
          if (interlude.afterParts <= 0) interludeAt = -0.5;
          else {
            let seen = 0;
            for (let i = 0; i < blocks.length; i += 1) {
              const b = blocks[i];
              seen += "parts" in b ? b.parts.length : 1;
              if (seen >= interlude.afterParts) {
                interludeAt = i;
                break;
              }
            }
          }
        }
        let lastActivityAt = -1;
        for (let i = blocks.length - 1; i >= 0; i -= 1) {
          if (blocks[i].kind === "activity") {
            lastActivityAt = i;
            break;
          }
        }
        let outcomePlaced = false;
        const rendered = (block: (typeof blocks)[number], index: number) => {
              switch (block.kind) {
                case "prose": {
                  const streamingNow = block.parts.some((part) => part.state === "streaming");
                  const split = sourcesTail && !streamingNow ? splitSourcesTail(proseOf(block.parts)) : null;
                  if (split) {
                    const bodyParts: Part[] = [{ ...block.parts[0], index: 0, type: "text", body: { text: split.body } }];
                    return (
                      <Fragment key={blockKey(block.parts, index)}>
                        <ProseBlock parts={bodyParts} codeInterceptor={codeInterceptor} streaming={false} />
                        {outcome && !outcomePlaced && ((outcomePlaced = true), <div data-lane-v2-outcome-slot="">{outcome}</div>)}
                        <SourcesReadBlock
                          count={sourcesReadCount(split, sourcesToolCalls)}
                          listMarkdown={split.listMarkdown}
                          queryCount={split.queryCount}
                          queriesMarkdown={split.queriesMarkdown}
                          orgFactsMarkdown={split.orgFactsMarkdown}
                          coverage={split.coverage}
                        />
                      </Fragment>
                    );
                  }
                  return (
                    <ProseBlock
                      key={blockKey(block.parts, index)}
                      parts={block.parts}
                      codeInterceptor={codeInterceptor}
                      /*
                       * THE LIVE EDGE IS A PART, NOT A POSITION.
                       *
                       * This was `streaming && index === blocks.length - 1`, so the cursor only
                       * appeared while prose happened to be the last block — and a `tool_call` after
                       * the text made it the second-to-last, which is exactly the shape the
                       * `streaming` fixture has. Result: no cursor for the whole of a mid-flight
                       * turn, seen in the browser. `Part.state` says which part is still arriving,
                       * which is the fact the cursor is about.
                       */
                      streaming={streamingNow}
                    />
                  );
                }
                case "reasoning": {
                  /*
                   * ── WHY "streaming" IS NOT JUST THIS BLOCK'S OWN PART STATE ─────────────────────
                   *
                   * Reported directly, and reproduced from the raw SSE: `groupTurnParts` starts a
                   * NEW reasoning block wherever a `tool_call` interrupts a run of reasoning parts,
                   * so a turn that reasons, calls a tool, then reasons again produces TWO separate
                   * `ReasoningBlock` instances. Gating each one's `streaming` on only ITS OWN parts'
                   * `state` meant the FIRST one closed the moment its own part settled to
                   * `available` — even though the run was still going and about to reason again. The
                   * reader watched "Thinking" open, close, then open again on a brand-new block a
                   * few seconds later: reported as "the UI is flickering".
                   *
                   * The fix is not to keep every past reasoning block open (a SETTLED one, superseded
                   * by a later block, is genuinely done and should read "Reasoning", collapsed) — it
                   * is to also stay open while THIS is the LAST block of a turn that is still
                   * `working` (parts exist, no visible prose yet, run not complete): that is exactly
                   * the gap between one reasoning segment settling and the next thing — another
                   * reasoning segment, a tool call, or the answer — actually starting. A block that
                   * is no longer last (something has been appended after it) or whose turn has moved
                   * past `working` (prose started, or the run finished) loses this and falls back to
                   * its own part state, which by then already says the honest thing.
                   */
                  const isLastBlock = index === blocks.length - 1;
                  const streaming =
                    block.parts.some((part) => part.state === "streaming") ||
                    (turnWorking && isLastBlock);
                  return (
                    <ReasoningBlock key={blockKey(block.parts, index)} parts={block.parts} streaming={streaming} />
                  );
                }
                case "activity":
                  return (
                    <ActivityBlock
                      key={blockKey(block.parts, index)}
                      parts={block.parts}
                      // The run's checks join the LAST tool-call group: one evidence panel per turn.
                      checks={index === lastActivityAt ? checks : undefined}
                    />
                  );
                case "error":
                  return <ErrorBlock key={block.part.part_id} part={block.part} />;
                case "reference": {
                  const target = referenceTargetOf(block.part);
                  return (
                    <ReferenceBlock
                      key={block.part.part_id}
                      part={block.part}
                      subtitle={target ? subtitles?.[target] ?? null : null}
                      previews={target === "changeset" && partPreviews?.previews.length ? partPreviews.previews : undefined}
                      previewsHidden={target === "changeset" ? partPreviews?.hidden : undefined}
                      activeRef={activeRef}
                      onOpen={onOpenWorkspace}
                      active={
                        !!target &&
                        activeSection === workspaceSectionOf(target) &&
                        // Only `artifact` is keyed on a ref; the rest have one subject per run, so
                        // a card with no ref is the open one whenever its section is.
                        (target !== "artifact" || (block.part.ref ?? null) === (activeRef ?? null))
                      }
                    />
                  );
                }
                default:
                  return null;
              }
        };
        return (
          <div className="flex flex-col gap-2">
            {interlude && interludeAt === -0.5 && <div>{interlude.node}</div>}
            {blocks.map((block, index) => (
              <Fragment key={`b-${index}`}>
                {rendered(block, index)}
                {interlude && interludeAt === index && <div>{interlude.node}</div>}
              </Fragment>
            ))}
            {interlude && interludeAt === -1 && <div>{interlude.node}</div>}

            {outcome && !outcomePlaced && <div data-lane-v2-outcome-slot="">{outcome}</div>}

            {/* Checks with no tool-call group to join get an evidence panel of their own. */}
            {lastActivityAt === -1 && !!checks?.length && (
              <ActivityBlock parts={[]} checks={checks} />
            )}

            {/*
              A PAST RUN'S OWN CARDS. Derived from the state it recorded (`Turn.references`), so the
              changeset a turn built stays under that turn once a later question has made it
              history. The live run's cards are derived by the transcript, not carried here.
            */}
            {references.length > 0 && (
              <div className="flex flex-col gap-2">
                {references.map((reference) => (
                  <DerivedReferenceBlock
                    key={reference.target}
                    reference={reference}
                    onOpen={onOpenWorkspace}
                    active={activeSection === workspaceSectionOf(reference.target)}
                    activeRef={activeRef}
                  />
                ))}
              </div>
            )}

            {/*
              THE SOURCES THIS TURN CITED. `groupParts` drops `citation` parts from the block flow —
              correctly, they are not a block — but nothing rendered them at all, so real citations
              with `verified` flags and component ids went on the floor. One row for the answer,
              where legacy puts its sources strip; `sourcesOf` dedupes on `citation_id`, so a source
              cited by two messages is one chip.
            */}
            <SourcesRow
              sources={sourcesOf(turn.assistant.flatMap((message) => message.parts ?? []))}
              onOpenComponent={(componentId) => onOpenWorkspace("changeset", componentId)}
            />
          </div>
        );
      })()}

      {/*
        ── HOW *THIS* RUN ENDED, UNDER *THIS* TURN ───────────────────────────────────────────────

        REPORTED: a second run failing put its error at the top of the whole conversation, above the
        first question — "the failure belongs to Run 2, not the entire conversation". The rail was a
        banner over the entire transcript reading the one live run; a past run had no ending stored
        at all (see `Turn.outcome`). Both halves are fixed by the ending travelling WITH the turn.

        ONLY ARCHIVED TURNS REACH THIS. `sessionTurns` stamps `outcome` on past runs only — the live
        run's rail and closing line are rendered once by `WeaveTranscript` after the last turn,
        which is the same place chronologically and already handles the feedback bar and follow-ups.
        Rendering both would show one failure twice.
      */}
      {turn.outcome && (
        <div className="flex flex-col gap-2">
          {/* Run-WIDE errors only; `routeErrors` sends anchored ones to the thing that failed. */}
          <RunErrorRailView errors={turn.outcome.errors} />
          {(() => {
            const closing = outcomeClosingLine(turn.outcome);
            // `ok` outcomes say nothing on a past turn: a finished answer that worked needs no
            // label, and the live turn's completion bar is where "done" belongs.
            return closing && closing.tone !== "ok" ? (
              <ClosingLine text={closing.text} tone={closing.tone} />
            ) : null;
          })()}
        </div>
      )}
    </article>
  );
});

/** Stable across a delta: the first part's id, which does not change as text accumulates. */
function blockKey(parts: Part[], index: number): string {
  return parts[0]?.part_id ?? `block-${index}`;
}

function referenceTargetOf(part: Part): ReferenceTarget | null {
  switch (part.type) {
    case "plan":
      return "plan";
    case "changeset":
    case "diff":
      return "changeset";
    case "deploy":
      return "deploy";
    case "evidence":
      return "evidence";
    case "artifact":
      return "artifact";
    default:
      return null;
  }
}

/** `ReferenceTarget` and `WorkspaceSection` overlap by design; this is the one-line bridge. */
function workspaceSectionOf(target: ReferenceTarget): OpenableSection {
  return target;
}

/** The closing line, styled by tone. Exported so the transcript can place it after the last turn. */
export function ClosingLine({
  text,
  tone,
  className,
}: {
  text: string;
  tone: "ok" | "warn" | "bad";
  className?: string;
}) {
  return (
    <p
      /*
       * `status`, not `alert`: the run finishing is the expected end of the thing the reader
       * started, even when the outcome is a failure — the failure itself is announced by the error
       * rail, which is assertive. Two assertive announcements for one event is one too many.
       */
      role="status"
      className={cn(
        "text-[13px]",
        tone === "bad"
          ? "text-[var(--ma-state-err-text)]"
          : tone === "warn"
            ? "text-[var(--ma-state-warn-text)]"
            : "text-muted-foreground",
        className,
      )}
    >
      {text}
    </p>
  );
}
